import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import { Select } from "@/components/design/Select";
import {
  GlobalSearchDocument,
  SearchEntityType,
} from "@/graphql/generated/operations";
import type { FieldOption } from "@/lib/field-util";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";
import {
  COMPANY_ROLES,
  COMPANY_ROLE_ADMIN,
  type CompanyRole,
} from "./company-roles";
import { CompanyMemberCount } from "./CompanyMemberCount";
import { CompanyPersonPill } from "./CompanyPersonPill";
import { COMPANY_SCOPE_MEMBER } from "./pending-scopes";
import { useCompanyMemberDraft } from "./use-company-member-draft";
import {
  MAX_CONTACT_PERSONS,
  type UseCompanyAdminResult,
} from "./use-company-admin";

// Member administration for one company (the company account model and §7):
// an admin adds EXISTING genesis people, sets their role, and removes them. There is no invitation,
// no consent step and no self-join in the MVP — §7 rules all three out — so nothing here waits for
// the other person to agree.
//
// THE LAST ADMIN IS NOT REMOVABLE OR DEMOTABLE. genesis refuses both under a row lock (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization
// §6): with no platform-role bypass, a company whose last admin went away would be editable by nobody
// and unrecoverable. That refusal is answered twice here, deliberately:
//
//   - before the click, by naming it on the row that would trigger it. The member list this page
//     already holds is the same set genesis counts, so the situation is knowable without asking.
//   - after the click, because the check above is a stale snapshot — a concurrent demotion elsewhere
//     makes a row that looked safe refuse. That path shows the same sentence rather than the
//     generic failure, via the refusal mapping in company-admin-error.
//
// THE ROSTER IS A PAGE, NOT THE ROSTER (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not), and that changes what may be derived
// from it. Two pre-click checks here used to count rows of a complete list:
//
//   - the CONTACT-PERSON CAP now counts `contactPersons`, which genesis serves complete and unpaged
//     (capped per company, §4). Counting the marked rows of the loaded pages would answer "how many
//     contacts are on screen" and unlock a mark past the cap that the server would refuse.
//   - the SOLE-ADMIN check cannot be answered from a page at all, so it is only made when
//     `rosterComplete` says every member is loaded. While the roster is partial the controls stay
//     enabled and genesis's own refusal does the work — which is the safe direction: a wrong "you
//     may not" locks an admin out of a legitimate change, a wrong "you may" costs one refused
//     request that is already worded for the user.
//
// CONTACT PERSONS ARE MARKED HERE, and the cap is answered the same way twice, for the same reason.
// The company page caps them (MAX_CONTACT_PERSONS), so:
//
//   - before the click, the mark control is disabled on rows that would exceed it, with the rule
//     stated as plain text on the row.
//   - after the click, because that count is a stale snapshot — another admin filling the last free
//     slot elsewhere makes a row that looked markable refuse. genesis catalogues that refusal
//     (GENESIS001241), so it is recognised exactly and shown as the rule rather than as a fault.
//
// ONLY MARKING IS EVER DISABLED. genesis never refuses an unmark, and the cap counts the OTHER
// marked members, so re-marking someone already marked is a no-op rather than a refusal — a full
// company must stay editable out of that state.
//
// The mark is NOT a role and must never be folded into the role select: the company account model fixes the
// vocabulary at admin/member, and a contact person is one of those who happens to be flagged. The
// two controls sit side by side and write different columns.
//
// The gate on this whole panel is presentation only. genesis re-checks the membership row on every
// one of these mutations and trusts nothing spark believes.
//
// NOTHING HERE WRITES ON THE CLICK ANY MORE. Adding, demoting, marking and removing are all STAGED
// (use-company-member-draft): they change the roster on screen and the SaveBar's entry, and they
// reach genesis when Save is pressed. Until then the whole edit view behaves as one preview — the
// same way the About-us box two sections up always has, and the way Kevin asked for the panel to
// behave: curate the roster, then commit it.
//
// WHAT THAT CHANGES ABOUT THE TWO PRE-CLICK CHECKS. Both now read the PROJECTED roster rather than
// the stored one, which is not a detail: staging "add Bea as admin" is precisely what makes "demote
// me" legitimate, and an admin has to be able to stage both before saving either. The server checks
// are unchanged and still decide — these only decide what is offered.
//
// THE CONFIRM DIALOG ON REMOVE IS GONE, deliberately. @adr SPARK-0011-destructive-actions-require-confirmation@1 asks for a step before something
// destructive; a staged removal destroys nothing — the row says so, Undo sits beside it, and leaving
// edit mode drops it. Save is the confirmation now.
//
// A PICKED PERSON IS STILL UNSAVED WORK, AND A TYPED SEARCH IS STILL NOT. Finding the right person
// in a search over everybody is the effort here, so a pick that has not been staged yet is
// registered with the PendingEdits registry; a half-typed name is not work worth stopping somebody
// over, and a guard that asks about one is a guard that gets clicked through.

/** genesis's role for "may administer this company". Compared exactly; genesis stores it lower-cased. */
const ROLE_ADMIN: CompanyRole = COMPANY_ROLE_ADMIN;

const SEARCH_LIMIT = 8;
const MIN_QUERY_LENGTH = 2;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseInput = any;

interface MemberRow {
  role: string;
  /** Orthogonal to `role` (the company page): a contact person is an admin or a member who is flagged. */
  contactPerson: boolean;
  /**
   * No avatar: the roster query asks for none, so every row here draws the monogram fallback, the
   * same one the strip and the company logo use.
   */
  person: {
    id: string;
    formattedName?: string | null;
  };
}

interface CompanyMemberEditorProps {
  /** The members LOADED SO FAR. genesis pages this (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not) — see `rosterComplete`. */
  members: readonly MemberRow[];
  /** The complete contact-person set, from genesis's own unpaged read. Counted for the §4 cap. */
  contactPersons: readonly { person: { id: string } }[];
  /** Whether `members` is every member. False while pages remain — see the note at the top. */
  rosterComplete: boolean;
  admin: UseCompanyAdminResult;
  /** See CompanyCompetencyEditor — the create state has one commit of its own and no SaveBar. */
  creating?: boolean;
  /** genesis's totalCount and the next-page control, rendered under the list. */
  count: number;
  hasMore: boolean;
  loadingMore: boolean;
  moreError: Error | null;
  onLoadMore: () => void;
}

export function CompanyMemberEditor({
  members,
  contactPersons,
  rosterComplete,
  admin,
  creating = false,
  count,
  hasMore,
  loadingMore,
  moreError,
  onLoadMore,
}: CompanyMemberEditorProps) {
  const intl = useIntl();
  const [message, setMessage] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [newRole, setNewRole] = useState<CompanyRole>("member");
  const draft = useCompanyMemberDraft(
    members,
    contactPersons,
    rosterComplete,
    admin,
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const term = debounced.trim();
  const search = useQuery(GlobalSearchDocument, {
    variables: {
      input: {
        query: term,
        entityTypes: [SearchEntityType.Person],
        take: SEARCH_LIMIT,
      } as LooseInput,
    },
    skip: term.length < MIN_QUERY_LENGTH,
  });

  // Keeps people who are already listed out of the add-a-person results. Only the LOADED members,
  // so on a partial roster somebody on a later page can still be offered — genesis answers that
  // with its duplicate refusal, which addMember already maps to the "already a member" sentence.
  // Hiding the control instead would need the whole roster, which is the thing paging removed.
  const memberIds = useMemo(
    // The PROJECTED roster: somebody staged for adding is on the list as far as the admin is
    // concerned, and offering them a second time would stage a duplicate genesis then refuses.
    () =>
      new Set(
        draft.projected.filter((row) => !row.isRemoved).map((row) => row.id),
      ),
    [draft.projected],
  );
  const candidates = useMemo(
    () =>
      (search.data?.globalSearch.results ?? [])
        .filter((result) => result.personId && !memberIds.has(result.personId))
        .map((result) => ({
          id: result.personId as string,
          label: result.label ?? result.personId!,
        })),
    [memberIds, search.data],
  );

  // Derived once per ERROR, not once per render. `userErrorMessage` logs an application diagnostic
  // on every call, and this message sits directly under a search box the admin keeps typing into —
  // rendered inline it emitted one diagnostic per keystroke for a single failed search.
  const searchErrorMessage = useMemo(
    () => (search.error ? userErrorMessage(intl, search.error) : null),
    [intl, search.error],
  );

  // The count genesis's requireAnotherAdmin makes: how many admins would be left. A company always
  // keeps at least one. Only meaningful over a COMPLETE roster — an admin on an unloaded page is an
  // admin genesis counts and this does not — so a partial roster answers "unknown" rather than a
  // number, and every row stays enabled.
  const adminCount = draft.adminCount;

  // The count genesis's requireContactPersonCapacity makes, from genesis's own complete answer
  // rather than from the marked rows on screen. Still a stale snapshot — it is what lets the control
  // be disabled before the click, not a substitute for the server check, which runs under a lock on
  // the company row.
  const contactSlotsFull = draft.contactSlotsFull;

  const roleOptions: FieldOption[] = COMPANY_ROLES.map((role) => ({
    value: role,
    label:
      role === ROLE_ADMIN
        ? intl.formatMessage({
            id: "company.admin.members.roleAdmin",
            defaultMessage: "Administrator",
          })
        : intl.formatMessage({
            id: "company.admin.members.roleMember",
            defaultMessage: "Member",
          }),
  }));

  /**
   * Stages the picked person with the role on screen. ONE path shared by the button and by the
   * registry's save, so "what Save stages" cannot drift from "what Add to company does".
   */
  function stagePicked() {
    // NOT REACHABLE, AND NOT REMOVABLE — the one line in this file coverage cannot reach. The Add
    // controls render only inside `{picked ? …}` and `saveOrThrow` calls this behind `if (picked)`,
    // so no caller can arrive here with nothing picked. It stays because it is also the narrowing
    // that lets `draft.stageAdd(picked, …)` typecheck: `picked` is state, and TypeScript cannot
    // narrow it across the closure without this. A test would have to call the function directly,
    // which would assert the compiler's behaviour rather than the component's.
    if (!picked) return;
    draft.stageAdd(picked, newRole);
    setPicked(null);
    setQuery("");
    setDebounced("");
    setNewRole("member");
    setMessage(null);
  }

  /** The visible button. Nothing is written here — the roster on screen changes and Save applies it. */
  function add() {
    stagePicked();
  }

  /**
   * The registry's save: stages a pick that has not been staged yet, then applies the whole draft.
   *
   * The role is always chosen — the select is never empty and defaults to `member` (the company account model
   * fixes the vocabulary at two) — so a pick is always something that CAN be written and this never
   * has to guess a value the admin did not pick.
   *
   * @throws when genesis refuses any part of the apply. A refused intent stays in the draft, so
   *   pressing Save again retries exactly what is left, and a caller holding a navigation must not
   *   proceed while something is still outstanding.
   */
  async function saveOrThrow(): Promise<void> {
    if (picked) stagePicked();
    // The pick is passed through rather than read back: `stagePicked` is a state update, so the
    // draft in this render still has the roster as it was a moment ago.
    const failures = await draft.apply(
      picked ? { id: picked.id, name: picked.label, role: newRole } : undefined,
    );
    setMessage(failures[0] ?? null);
    if (failures.length > 0) throw new LocalizedError(failures[0]!);
  }

  /**
   * Drops the pick, the role chosen with it, and every staged change to the roster. The search text
   * stays: cancelling is not cancelling the search, and the candidate list comes straight back.
   */
  function discardPick() {
    setPicked(null);
    setNewRole("member");
    setMessage(null);
    draft.discard();
  }

  usePendingScope(COMPANY_SCOPE_MEMBER, {
    // Either kind of unsaved work: a person picked in the form, or anything staged on the roster.
    // Silent while the company is still hypothetical — see CompanyEditingScopes.
    dirty: !creating && (picked !== null || draft.dirty),
    save: saveOrThrow,
    discard: discardPick,
  });

  return (
    <div className="flex flex-col gap-5">
      {draft.projected.length === 0 ? (
        <p id="company-member-empty" className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="company.page.members.empty"
            defaultMessage="Nobody is listed yet."
          />
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {/* Stated ONCE for the whole list, not per row — unlike the sole-admin note, which marks
              the single row it applies to, the cap disables every unmarked row at the same instant
              and for the same single reason. Repeating it on each of them would be noise.

              Neutral: plain text, no colour, no ring. It is the rule, not a failure. */}
          {contactSlotsFull ? (
            <li id="company-member-contactlimit" className="smycles-field-meta text-ink-muted">
              <FormattedMessage
                id="company.admin.members.contactPersonLimit"
                defaultMessage="All {max, number} contact persons are marked. Unmark one to mark somebody else."
                values={{ max: MAX_CONTACT_PERSONS }}
              />
            </li>
          ) : null}
          {draft.projected.map((member) => {
            const isSoleAdmin =
              member.role === ROLE_ADMIN &&
              adminCount === 1 &&
              !member.isRemoved;
            const name = member.name;
            // Only an UNMARKED member can be blocked by the cap. An already-marked one is excluded
            // from the count, so unmarking — and re-marking — always stays available.
            const contactBlocked = !member.contactPerson && contactSlotsFull;
            const staged = member.pending.length > 0;
            return (
              <li key={member.id} id={`company-member-row-${member.id}`} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-3">
                  {/* A CHIP, THE WIDTH OF THE NAME. `flex-1` made this pill eat every pixel the
                      row's controls did not, so one person rendered as a bar running the width of
                      the page with an avatar marooned at its left end. `constrained` is what the
                      shared pill calls that difference: it shares its row here and nowhere else.

                      No avatar: the draft's projection carries a name and an id, and a person staged
                      from the search has no face to show yet either. */}
                  <CompanyPersonPill id={member.id} name={name} constrained />
                  {/* THE CONTROLS ARE A RIGHT-ALIGNED GROUP, not three more items in the row's flow.
                      The name pill is deliberately the width of the name, so laying the controls out
                      after it started each row at a different x and the column read as ragged. `ml-auto`
                      on one wrapper puts every row's controls against the same right edge while the
                      pill keeps its own width.

                      NO `flex-wrap` INSIDE THE GROUP, and no shrinking: with it, the three controls
                      broke onto separate lines the moment the name was long enough to crowd them, so
                      Remove sat under Contact person instead of beside it. The row's own wrap handles
                      a narrow viewport by dropping the WHOLE group to the next line, which keeps the
                      three together and still right-aligned. */}
                  <div className="ml-auto flex shrink-0 items-center gap-3">
                  {/* THE WIDTH GOES ON A WRAPPER, NOT ON THE SELECT, and that is what keeps the row
                      inside its card. <Select>'s base class is `w-full`. `max-w-44` on the select
                      itself capped the RENDERED width at 176px but left its INTRINSIC width the width
                      of its longest option (~130px) — so the flex group above sized itself from 130
                      and then had to hold 176, and the whole group hung 46px past the card's padding,
                      Remove sitting outside the box. Measured, not guessed: a real engine reported the
                      group's box ending at the content edge while its last button ended 45px beyond it.

                      A definite `w-44` on a wrapper makes the select's contribution and its rendered
                      width the same number, which is also why the select no longer carries a width of
                      its own — two width utilities on one element leave the winner to Tailwind's sort
                      order, which is the bug this replaced. */}
                  <div className="w-44 shrink-0">
                    <Select
                      id={`company-member-role-${member.id}`}
                      value={member.role}
                      onChange={(next) =>
                        draft.stageRole(member.id, next as CompanyRole)
                      }
                      options={roleOptions}
                      hideEmpty
                      disabled={isSoleAdmin || member.isRemoved}
                    />
                  </div>
                  {/* A toggle, not a checkbox and not a role option: it writes its own column.
                      `aria-pressed` is what tells a screen reader whether this member IS a contact
                      person — the label does not change. */}
                  <Button
                    id={`company-member-contact-${member.id}`}
                    variant="toggle"
                    size="sm"
                    // A ROUNDED PILL MUST NOT WRAP. `smycles-toggle-button` is `inline-block` with no
                    // whitespace rule, so a tight row broke "Contact person" across two lines inside the
                    // pill and the capsule grew into a lozenge. Applied here rather than on the shared
                    // class: changing a primitive is a system change (docs/UI_CONVENTIONS.md), and this
                    // row is the only place the label is long enough to reach the edge.
                    className="whitespace-nowrap"
                    aria-pressed={member.contactPerson}
                    disabled={contactBlocked || member.isRemoved}
                    onClick={() =>
                      draft.stageContact(member.id, !member.contactPerson)
                    }
                  >
                    <FormattedMessage
                      id="company.admin.members.contactPerson"
                      defaultMessage="Contact person"
                    />
                  </Button>
                  {/* ONE SLOT, TWO LABELS, ONE WIDTH. This button is "Undo" on a staged row and
                      "Remove" everywhere else, and the group is right-aligned — so a narrower label
                      pulled that row's whole set of controls rightwards and the column read as
                      ragged the moment anybody staged a removal. `min-w` rather than a fixed `w`
                      because the floor only has to clear the longest of the four labels the two
                      bundles can produce: measured at Remove 65, Undo 51, Entfernen 74, Rückgängig
                      84, so 5.5rem (88px) covers all of them and a longer future label still grows
                      the slot instead of being clipped. */}
                  {staged ? (
                    <Button
                      id={`company-member-undo-${member.id}`}
                      variant="secondary"
                      size="sm"
                      className="min-w-[5.5rem]"
                      onClick={() => draft.unstage(member.id)}
                    >
                      <FormattedMessage
                        id="common.undo"
                        defaultMessage="Undo"
                      />
                    </Button>
                  ) : (
                    <Button
                      id={`company-member-remove-${member.id}`}
                      variant="secondary"
                      size="sm"
                      className="min-w-[5.5rem]"
                      disabled={isSoleAdmin}
                      onClick={() => draft.stageRemove(member.id)}
                    >
                      <FormattedMessage
                        id="company.admin.members.remove"
                        defaultMessage="Remove"
                      />
                    </Button>
                  )}
                  </div>
                </div>
                {/* Textual, neutral, and one line per thing that is true of this row. A staged row
                    is not an error and not a warning — it is a statement about what Save will do —
                    so it carries no colour and no ring, exactly as the sole-admin note does.

                    Given a reading measure like every other sentence on this page: these are the
                    last controls-column text that still ran the full width of the box. */}
                <div className="max-w-[42rem]">
                {member.pending.includes("add") ? (
                  <p id={`company-member-pendingadd-${member.id}`} className="smycles-field-meta text-ink-muted">
                    <FormattedMessage
                      id="company.admin.members.pendingAdd"
                      defaultMessage="Will be added when you save."
                    />
                  </p>
                ) : null}
                {member.pending.includes("remove") ? (
                  <p id={`company-member-pendingremove-${member.id}`} className="smycles-field-meta text-ink-muted">
                    <FormattedMessage
                      id="company.admin.members.pendingRemove"
                      defaultMessage="Will be removed when you save."
                    />
                  </p>
                ) : null}
                {member.pending.includes("role") ? (
                  <p id={`company-member-pendingrole-${member.id}`} className="smycles-field-meta text-ink-muted">
                    <FormattedMessage
                      id="company.admin.members.pendingRole"
                      defaultMessage="The new role applies when you save."
                    />
                  </p>
                ) : null}
                {member.pending.includes("contact") ? (
                  <p id={`company-member-pendingcontact-${member.id}`} className="smycles-field-meta text-ink-muted">
                    <FormattedMessage
                      id="company.admin.members.pendingContact"
                      defaultMessage="The contact-person mark applies when you save."
                    />
                  </p>
                ) : null}
                {isSoleAdmin ? (
                  <p id={`company-member-soleadmin-${member.id}`} className="smycles-field-meta text-ink-muted">
                    <FormattedMessage
                      id="company.admin.members.soleAdmin"
                      defaultMessage="The only administrator. Make somebody else an administrator to change or remove this one."
                    />
                  </p>
                ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* The same count and the same next-page control the read-only strip carries. An admin has to
          be able to reach member thirteen to remove them, and a second way of asking for the next
          page would be a second thing to keep right. */}
      <CompanyMemberCount
        shown={members.length}
        total={count}
        hasMore={hasMore}
        loading={loadingMore}
        error={moreError}
        onLoadMore={onLoadMore}
      />

      <div className="flex flex-col gap-3 border-t border-ink/5 pt-4">
        <label className="smycles-field-label" htmlFor="company-member-search">
          <FormattedMessage
            id="company.admin.members.addTitle"
            defaultMessage="Add a person"
          />
        </label>
        <p id="company-member-addhint" className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="company.admin.members.addHint"
            defaultMessage="Puts an existing person on the list. They are not asked first, and nothing is written until you save."
          />
        </p>
        <input
          id="company-member-search"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPicked(null);
          }}
          disabled={admin.busy}
          placeholder={intl.formatMessage({
            id: "company.admin.members.searchPlaceholder",
            defaultMessage: "Search people by name",
          })}
          className="smycles-field-value w-full rounded-md bg-surface px-3 py-2.5 ring-1 ring-ink/10 placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-ink-accent-ui disabled:opacity-50"
        />

        {term.length >= MIN_QUERY_LENGTH && !picked ? (
          search.loading && candidates.length === 0 ? (
            <p className="smycles-field-meta text-ink-muted">
              <FormattedMessage
                id="company.admin.members.searching"
                defaultMessage="Searching people"
              />
            </p>
          ) : search.error ? (
            // A FAILED search is not an empty one (#316). Without this branch an error left
            // `data` empty, so the sentence below claimed there is nobody who matches — an answer
            // the search never gave. The two send an admin opposite ways: "nobody matches" means
            // stop looking, "the search did not run" means retry. The same distinction the roster
            // already draws between withheld and empty, one query further on.
            //
            // IT IS TESTED BEFORE THE LIST, not inside the empty case. `fetchPolicy` is
            // cache-and-network, so a cached page plus a later transport failure would otherwise
            // leave rows on screen and swallow the failure — presenting a stale list as the answer
            // to a search that did not run. Loading still wins over both, so a retry says
            // "searching" rather than keeping the old error up.
            //
            // The message is OURS, never the server's (@adr SPARK-0010-no-raw-server-errors-in-ui@1):
            // `userErrorMessage` logs the raw error, keeps genesis's wording out of the UI, and
            // quotes its errorId as a support code when one came back.
            <p className="smycles-field-meta text-ink-muted">{searchErrorMessage}</p>
          ) : candidates.length === 0 ? (
            <p id="company-member-nohits" className="smycles-field-meta text-ink-muted">
              <FormattedMessage
                id="company.admin.members.noPeople"
                defaultMessage="Nobody matches who is not already listed."
              />
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {candidates.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    id={`company-member-hit-${candidate.id}`}
                    type="button"
                    onClick={() => setPicked(candidate)}
                    className="w-full rounded-md px-3 py-2 text-left text-smycles-body text-ink hover:bg-ink/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
                  >
                    {candidate.label}
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {picked ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-smycles-body text-ink">{picked.label}</span>
            <Select
              value={newRole}
              onChange={(next) => setNewRole(next as CompanyRole)}
              options={roleOptions}
              hideEmpty
              disabled={admin.busy}
              className="max-w-44"
            />
            <Button
              id="company-member-add"
              size="compact"
              disabled={admin.busy}
              onClick={() => void add()}
            >
              <FormattedMessage
                id="company.admin.members.add"
                defaultMessage="Add to company"
              />
            </Button>
            <Button variant="secondary" size="compact" onClick={discardPick}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          </div>
        ) : null}
      </div>

      {message ? <p className="smycles-field-meta text-ink">{message}</p> : null}
    </div>
  );
}
